import React, { useState, useEffect, Fragment } from 'react';
import { useAuth } from '../context/AuthContext';
import dataService from '../services/dataService'; // Ensure this path is correct
import { toast } from 'react-toastify';
import {
    PlusIcon,
    MagnifyingGlassIcon,
    TrashIcon,
    EyeIcon,
    UserGroupIcon,
    CheckCircleIcon,
    ClockIcon,
    XCircleIcon,
    ShareIcon,
    PencilIcon,
    UserPlusIcon,
    XMarkIcon,
    CalendarIcon,
    LinkIcon
} from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import ScheduleTrainingModal from '../components/ScheduleTrainingModal';
import ViewDetailsModal from '../components/ViewDetailsModal';
import TrainingRegistrationModal from '../components/TrainingRegistrationModal';
import { copyToClipboard } from '../utils/clipboard';

const WhatsAppIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

const FacebookIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
);

const Training = () => {
    const { user } = useAuth();
    const activeRole = sessionStorage.getItem('activeRole');
    const isActingAsChapterAdmin = user?.role === 'chapter_admin' && activeRole !== 'member';
    const isOrgAdmin = user?.role === 'admin';
    const [trainings, setTrainings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('upcoming'); // upcoming, past, all
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [trainingToEdit, setTrainingToEdit] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [activeShareId, setActiveShareId] = useState(null);

    useEffect(() => {
        const handleOutsideClick = () => {
            setActiveShareId(null);
        };
        document.addEventListener('click', handleOutsideClick);
        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, []);

    // Registrants Modal State
    const [isRegistrantsModalOpen, setIsRegistrantsModalOpen] = useState(false);
    const [selectedTrainingId, setSelectedTrainingId] = useState(null);
    const [registrants, setRegistrants] = useState([]);
    const [loadingRegistrants, setLoadingRegistrants] = useState(false);

    // Training Details Modal State
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedTraining, setSelectedTraining] = useState(null);

    // Registration Modal State
    const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
    const [trainingToRegister, setTrainingToRegister] = useState(null);

    // Visitor Modal State
    const [isVisitorModalOpen, setIsVisitorModalOpen] = useState(false);
    const [chapters, setChapters] = useState([]);
    const [isVisitorSubmitting, setIsVisitorSubmitting] = useState(false);
    const [visitorForm, setVisitorForm] = useState({
        name: '',
        email: '',
        contact_number: '',
        company_name: '',
        chapter: '',
        payment_confirmed: false
    });

    const handleAddVisitor = async (e) => {
        e.preventDefault();
        if (isVisitorSubmitting) return;
        setIsVisitorSubmitting(true);
        try {
            await dataService.registerExternalForTraining(selectedTraining.id, visitorForm);
            toast.success('Visitor registered successfully for this training');
            setIsVisitorModalOpen(false);
            setVisitorForm({ name: '', email: '', contact_number: '', company_name: '', chapter: '', payment_confirmed: false });
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Failed to add visitor');
        } finally {
            setIsVisitorSubmitting(false);
        }
    };

    useEffect(() => {
        fetchTrainings();
        fetchChapters();
    }, []);

    const fetchChapters = async () => {
        try {
            const res = await dataService.getChapters();
            if (res.data && res.data.success) {
                setChapters(res.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch chapters', error);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, filter]);

    const fetchTrainings = async () => {
        setLoading(true);
        try {
            const res = await dataService.getTrainings();
            if (res.data.success) {
                setTrainings(res.data.data);
            }
        } catch (error) {
            console.error('Failed to load trainings', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this training?')) return;
        try {
            const res = await dataService.deleteTraining(id);
            if (res.data.success) {
                toast.success('Training deleted');
                fetchTrainings();
            } else {
                toast.error(res.data.message || 'Failed to delete training');
            }
        } catch (err) {
            toast.error('Failed to delete training');
        }
    };

    const handleViewRegistrants = async (training) => {
        if (user?.role !== 'admin' && !isActingAsChapterAdmin) {
            toast.error("Access denied");
            return;
        }
        setSelectedTrainingId(training.id);
        setIsRegistrantsModalOpen(true);
        setLoadingRegistrants(true);
        try {
            const res = await dataService.getTrainingRegistrants(training.id);
            setRegistrants(res.data.data);
        } catch (error) {
            console.error('Failed to load registrants', error);
        } finally {
            setLoadingRegistrants(false);
        }
    };

    const handleViewDetails = (training) => {
        setSelectedTraining(training);
        setIsDetailsModalOpen(true);
    };

    const handleRegister = (training) => {
        setTrainingToRegister(training);
        setIsRegistrationModalOpen(true);
    };

    const handleEdit = (training) => {
        setTrainingToEdit(training);
        setIsEditMode(true);
        setIsScheduleModalOpen(true);
    };

    const handleShare = async (training) => {
        const publicUrl = `${window.location.origin}/public/training/${training.id}`;
        const success = await copyToClipboard(publicUrl);
        if (success) {
            toast.success('Training link copied to clipboard!');
        } else {
            toast.error('Failed to copy link. Please copy it manually.');
        }
    };

    const handleTrainingCreated = (newTraining) => {
        setIsScheduleModalOpen(false);
        fetchTrainings();
    };

    const filteredTrainings = trainings.filter(training => {
        const trainingDate = new Date(training.training_start_date);
        const now = new Date();
        const matchesFilter =
            filter === 'all' ? true :
                filter === 'upcoming' ? trainingDate >= now :
                    trainingDate < now;

        const matchesSearch = training.training_title.toLowerCase().includes(searchQuery.toLowerCase());

        return matchesFilter && matchesSearch;
    });

    const itemsPerPage = 12;
    const totalPages = Math.ceil(filteredTrainings.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedTrainings = filteredTrainings.slice(startIndex, endIndex);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric'
        });
    };

    const stripHtml = (htmlString) => {
        if (!htmlString) return '';
        let text = htmlString;
        text = text.replace(/<br\s*\/?>/gi, '\n');
        text = text.replace(/<\/p>/gi, '\n');
        text = text.replace(/<\/div>/gi, '\n');
        text = text.replace(/<\/li>/gi, '\n');
        text = text.replace(/<li>/gi, '• ');
        text = text.replace(/<\/h[1-6]>/gi, '\n');
        text = text.replace(/<(?:strong|b)>/gi, '*');
        text = text.replace(/<\/(?:strong|b)>/gi, '*');
        text = text.replace(/<(?:em|i)>/gi, '_');
        text = text.replace(/<\/(?:em|i)>/gi, '_');
        text = text.replace(/<(?:strike|del|s)>/gi, '~');
        text = text.replace(/<\/(?:strike|del|s)>/gi, '~');
        text = text.replace(/<code>/gi, '```');
        text = text.replace(/<\/code>/gi, '```');
        text = text.replace(/<a\s+(?:[^>]*?\s+)?href="([^"]*)"[^>]*>(.*?)<\/a>/gi, (match, url, linkText) => {
            const cleanUrl = url.trim();
            const cleanText = linkText.trim();
            if (cleanUrl === cleanText || cleanText.includes(cleanUrl) || cleanUrl.includes(cleanText)) {
                return cleanUrl;
            }
            return `${cleanText} (${cleanUrl})`;
        });
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        let cleanText = tempDiv.textContent || tempDiv.innerText || '';
        cleanText = cleanText.replace(/\n{3,}/g, '\n\n');
        return cleanText.trim();
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Training Management</h1>
                    <p className="text-gray-500">Schedule and manage organization trainings</p>
                </div>
                {(user?.role === 'admin' || isActingAsChapterAdmin) && (
                    <button
                        onClick={() => {
                            setTrainingToEdit(null);
                            setIsEditMode(false);
                            setIsScheduleModalOpen(true);
                        }}
                        className="flex items-center gap-2 bg-[#4bb1f9] text-white px-4 py-2 rounded-lg hover:bg-[#3ca1e9] transition-all shadow-md font-medium"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Schedule Training
                    </button>
                )}
            </div>

            {/* Filters & Search */}
            <div className="flex flex-col sm:flex-row gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center bg-gray-50 rounded-lg px-3 py-2 border border-gray-200 flex-1 relative">
                    <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 mr-2" />
                    <input
                        type="text"
                        placeholder="Search trainings..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bg-transparent border-none outline-none text-sm w-full text-gray-700 pr-8"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
                <div className="flex gap-2">
                    {['upcoming', 'past', 'all'].map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all ${filter === f
                                ? "bg-primary-50 text-primary-700 border border-primary-200"
                                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                                }`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>

            {/* Unified Premium Card Grid (Desktop & Mobile) */}
            <div className="w-full">
                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                ) : filteredTrainings.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-3xl border border-slate-100 text-slate-400 font-bold uppercase tracking-widest opacity-60">
                        No trainings found matching your criteria.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {paginatedTrainings.map((training) => {
                            const isPast = new Date(training.training_start_date) < new Date().setHours(0, 0, 0, 0);
                            return (
                                <div
                                    key={training.id}
                                    className="group relative bg-white border-2 border-slate-100 rounded-3xl p-6 hover:border-primary-200 hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
                                >
                                    <div>
                                        {/* Header */}
                                        <div className="flex items-start justify-between gap-4 mb-4">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center border-2 border-white shadow-md ring-2 ring-slate-50 flex-shrink-0">
                                                    <CalendarIcon className="w-6 h-6 text-primary-600" />
                                                </div>
                                                <div className="min-w-0">
                                                    <h3 className="text-base font-black text-slate-900 group-hover:text-primary-600 transition-colors truncate" title={training.training_title}>
                                                        {training.training_title}
                                                    </h3>
                                                    <p className="text-xs font-semibold text-slate-500 mt-0.5 line-clamp-2">
                                                        {training.training_description}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className={`shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider ${
                                                training.training_mode === 'Virtual'
                                                    ? 'bg-blue-50 text-blue-700 border border-blue-100'
                                                    : 'bg-green-50 text-green-700 border border-green-100'
                                            }`}>
                                                {training.training_mode || 'In-Person'}
                                            </span>
                                        </div>

                                        {/* Details Grid */}
                                        <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-100">
                                            <div>
                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                    Start Date
                                                </p>
                                                <p className="text-sm font-bold text-slate-700">
                                                    {formatDate(training.training_start_date)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                    Start Time
                                                </p>
                                                <p className="text-sm font-bold text-slate-700">
                                                    {training.training_start_time ? `${training.training_start_time.slice(0, 5)}` : 'TBA'}
                                                </p>
                                            </div>
                                            {(user?.role === 'admin' || isActingAsChapterAdmin) && (
                                                <div className="col-span-2">
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                        Registrations
                                                    </p>
                                                    <button
                                                        onClick={() => handleViewRegistrants(training)}
                                                        className="text-primary-600 hover:text-primary-800 text-xs font-bold uppercase tracking-wider flex items-center gap-1 active:scale-95 transition-all"
                                                    >
                                                        <UserGroupIcon className="w-4 h-4 text-primary-500" />
                                                        View Registrations
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Actions Footer */}
                                    <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100 mt-auto">
                                        {/* View Details - Always shown */}
                                        <button
                                            onClick={() => handleViewDetails(training)}
                                            className="p-2.5 bg-white border border-slate-200 text-slate-400 rounded-xl hover:text-primary-600 hover:bg-primary-50 hover:border-primary-200 transition-all shadow-sm active:scale-95"
                                            title="View Details"
                                        >
                                            <EyeIcon className="w-4 h-4" />
                                        </button>

                                         {/* Share Training - Only if NOT past */}
                                         {!isPast && (
                                             <div className="relative">
                                                 <button
                                                     onClick={(e) => {
                                                         e.stopPropagation();
                                                         if (isOrgAdmin || isActingAsChapterAdmin) {
                                                             setActiveShareId(activeShareId === training.id ? null : training.id);
                                                         } else {
                                                             handleShare(training);
                                                         }
                                                     }}
                                                     className="p-2.5 text-slate-400 hover:text-green-600 hover:bg-green-50 border border-slate-200 hover:border-green-100 rounded-xl transition-all active:scale-95"
                                                     title="Share Training"
                                                 >
                                                     <ShareIcon className="w-4 h-4" />
                                                 </button>

                                                 {(isOrgAdmin || isActingAsChapterAdmin) && activeShareId === training.id && (
                                                     <div 
                                                         onClick={(e) => e.stopPropagation()}
                                                         className="absolute bottom-12 right-0 z-50 w-40 bg-white border border-slate-100 rounded-2xl shadow-xl p-1.5 flex flex-col gap-1"
                                                     >
                                                         <button
                                                             onClick={() => {
                                                                 const publicUrl = `${window.location.origin}/public/training/${training.id}`;
                                                                 const title = training.title || training.training_title || training.training_topic || 'N/A';
                                                                 const description = stripHtml(training.description || training.training_description) || 'No description provided.';
                                                                 const date = training.training_start_date || training.start_date;
                                                                 const trainingDateFormatted = date ? formatDate(date) : 'TBA';
                                                                 const time = training.training_start_time || training.training_time_in || training.start_time || 'TBA';
                                                                 const mode = training.training_mode || training.mode || 'In-Person';
                                                                 const location = training.training_location || training.location;
                                                                 const trainingLocation = mode !== 'Virtual' && location ? `\nMode: In-Person\nVenue: ${location}` : `\nMode: Virtual`;
                                                                 const charges = Number(training.training_charges || 0);
                                                                 const paymentOption = (charges > 0 && training.payment_link) ? `\nPayment Link (if paying now): \n${training.payment_link}` : '';
                                                                 
                                                                 const shareText = `Training Invitation\n\nTopic: ${title}\nDescription: ${description}\nDate: ${trainingDateFormatted}\nTime: ${time}${trainingLocation}${paymentOption}\n\nRegister Here: \n${publicUrl}`;
                                                                 
                                                                 const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                                                                 const whatsappUrl = isMobile 
                                                                     ? `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`
                                                                     : `https://web.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
                                                                     
                                                                 window.open(whatsappUrl, '_blank');
                                                                 setActiveShareId(null);
                                                             }}
                                                             className="w-full flex items-center gap-2 px-2.5 py-2 text-xs font-bold text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 rounded-xl transition-all text-left"
                                                         >
                                                             <WhatsAppIcon className="w-4.5 h-4.5 text-emerald-500" />
                                                             WhatsApp
                                                         </button>
                                                         <button
                                                             onClick={() => {
                                                                 const publicUrl = `${window.location.origin}/public/training/${training.id}`;
                                                                 const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}`;
                                                                 window.open(facebookUrl, '_blank');
                                                                 setActiveShareId(null);
                                                             }}
                                                             className="w-full flex items-center gap-2 px-2.5 py-2 text-xs font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-700 rounded-xl transition-all text-left"
                                                         >
                                                             <FacebookIcon className="w-4.5 h-4.5 text-blue-600" />
                                                             Facebook
                                                         </button>
                                                         <button
                                                             onClick={() => {
                                                                 handleShare(training);
                                                                 setActiveShareId(null);
                                                             }}
                                                             className="w-full flex items-center gap-2 px-2.5 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 hover:text-slate-900 border-t border-slate-100 mt-0.5 pt-1.5 rounded-xl transition-all text-left"
                                                         >
                                                             <LinkIcon className="w-4.5 h-4.5 text-slate-400" />
                                                             Copy Link
                                                         </button>
                                                     </div>
                                                 )}
                                             </div>
                                         )}

                                        {/* Add Visitor - Only if NOT past */}
                                        {!isPast && (
                                            <button
                                                onClick={() => {
                                                    setSelectedTraining(training);
                                                    setIsVisitorModalOpen(true);
                                                }}
                                                className="p-2.5 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-100 rounded-xl transition-all active:scale-95"
                                                title="Add Visitor"
                                            >
                                                <UserPlusIcon className="w-4 h-4" />
                                            </button>
                                        )}

                                        {/* Edit and Delete - Only if NOT past AND user is the creator */}
                                        {!isPast && Number(training.created_by) === Number(user?.id) && (
                                            <>
                                                <button
                                                    onClick={() => handleEdit(training)}
                                                    className="p-2.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 border border-slate-200 hover:border-amber-100 rounded-xl transition-all active:scale-95"
                                                    title="Edit Training"
                                                >
                                                    <PencilIcon className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(training.id)}
                                                    className="p-2.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 border border-slate-200 hover:border-rose-100 rounded-xl transition-all active:scale-95"
                                                    title="Delete Training"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Pagination Controls */}
            {filteredTrainings.length > 0 && (
                <div className="flex flex-col md:flex-row items-center justify-between mt-6 pt-6 border-t border-slate-100 gap-4">
                    <div className="text-sm font-semibold text-slate-600">
                        Showing <span className="font-black text-slate-900">{Math.min(endIndex, filteredTrainings.length)}</span> of{' '}
                        <span className="font-black text-slate-900">{filteredTrainings.length}</span> {filteredTrainings.length === 1 ? 'training' : 'trainings'}
                    </div>

                    {filteredTrainings.length > itemsPerPage && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === 1
                                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-transparent'
                                    : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                    }`}
                            >
                                Previous
                            </button>

                            <div className="flex items-center gap-1">
                                {[...Array(totalPages)].map((_, index) => {
                                    const pageNum = index + 1;
                                    if (
                                        pageNum === 1 ||
                                        pageNum === totalPages ||
                                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                    ) {
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`w-10 h-10 rounded-xl font-black text-sm transition-all ${currentPage === pageNum
                                                    ? 'bg-[#4eb7f5] text-white shadow-lg shadow-blue-100'
                                                    : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                        return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                                    }
                                    return null;
                                })}
                            </div>

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === totalPages
                                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-transparent'
                                    : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                    }`}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Schedule/Edit Training Modal */}
            <ScheduleTrainingModal
                isOpen={isScheduleModalOpen}
                onClose={() => {
                    setIsScheduleModalOpen(false);
                    setTrainingToEdit(null);
                    setIsEditMode(false);
                }}
                onTrainingCreated={() => {
                    handleTrainingCreated();
                    setTrainingToEdit(null);
                    setIsEditMode(false);
                }}
                isEdit={isEditMode}
                trainingData={trainingToEdit}
            />

            {/* Registrants Modal */}
            {isRegistrantsModalOpen && (
                <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[80vh] animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <h3 className="text-lg font-bold text-gray-900">Registered Members</h3>
                            <button onClick={() => setIsRegistrantsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <span className="sr-only">Close</span>
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-0 overflow-y-auto flex-1">
                            {loadingRegistrants ? (
                                <div className="p-12 flex justify-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                </div>
                            ) : registrants.length === 0 ? (
                                <div className="p-12 text-center text-gray-500">
                                    <UserGroupIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                    <p>No members registered yet.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="hidden md:block">
                                        <table className="w-full text-left border-collapse">
                                            <thead className="bg-gray-50 sticky top-0">
                                                <tr className="text-xs uppercase text-gray-500 font-semibold tracking-wider">
                                                    <th className="px-6 py-3 border-b">Member Name</th>
                                                    <th className="px-6 py-3 border-b">Contact</th>
                                                    <th className="px-6 py-3 border-b">Type</th>
                                                    <th className="px-6 py-3 border-b">Payment Status</th>
                                                    <th className="px-6 py-3 border-b">Registered On</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {registrants.map((reg, idx) => (
                                                    <tr key={reg.id || idx} className="hover:bg-gray-50">
                                                        <td className="px-6 py-3 text-sm font-medium text-gray-900">{reg.name}</td>
                                                        <td className="px-6 py-3 text-sm text-gray-500">
                                                            <div className="text-gray-900 font-medium">{reg.email}</div>
                                                            <div className="text-xs text-gray-400">{reg.contact_number || 'N/A'}</div>
                                                        </td>
                                                        <td className="px-6 py-3">
                                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${reg.registrant_type === 'external'
                                                                ? 'bg-purple-100 text-purple-800'
                                                                : 'bg-blue-100 text-blue-800'
                                                                }`}>
                                                                {reg.registrant_type === 'external' ? 'External' : 'Member'}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-3">
                                                            {reg.payment_status === 'completed' || reg.payment_confirmed ? (
                                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                    <CheckCircleIcon className="w-4 h-4" />
                                                                    Paid
                                                                </span>
                                                            ) : reg.payment_status === 'pending' ? (
                                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                                                    <ClockIcon className="w-4 h-4" />
                                                                    Pending
                                                                </span>
                                                            ) : reg.payment_status === 'failed' ? (
                                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                    <XCircleIcon className="w-4 h-4" />
                                                                    Failed
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs text-gray-400">N/A</span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-3 text-sm text-gray-500">{new Date(reg.registration_date).toLocaleDateString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile Card View */}
                                    <div className="md:hidden space-y-4 p-4">
                                        {registrants.map((reg, idx) => (
                                            <div key={reg.id || idx} className="bg-slate-50 rounded-2xl p-4 border border-slate-100 shadow-sm space-y-3">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <h4 className="text-sm font-black text-slate-900">{reg.name}</h4>
                                                        <p className="text-xs font-semibold text-slate-500">{reg.email}</p>
                                                    </div>
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider ${reg.registrant_type === 'external'
                                                        ? 'bg-purple-50 text-purple-700 border border-purple-100'
                                                        : 'bg-blue-50 text-blue-700 border border-blue-100'
                                                        }`}>
                                                        {reg.registrant_type === 'external' ? 'External' : 'Member'}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-center text-[11px] font-bold">
                                                    <div className="text-slate-500 truncate mr-2">
                                                        {reg.contact_number || 'N/A'}
                                                    </div>
                                                    <div className="text-slate-400">
                                                        {new Date(reg.registration_date).toLocaleDateString()}
                                                    </div>
                                                </div>
                                                <div className="pt-2 border-t border-slate-200">
                                                    {reg.payment_status === 'completed' || reg.payment_confirmed ? (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-100">
                                                            <CheckCircleIcon className="w-3.5 h-3.5" />
                                                            Payment Completed
                                                        </span>
                                                    ) : reg.payment_status === 'pending' ? (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-100">
                                                            <ClockIcon className="w-3.5 h-3.5" />
                                                            Awaiting Payment
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-red-50 text-red-700 border border-red-100">
                                                            <XCircleIcon className="w-3.5 h-3.5" />
                                                            Payment Failed
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="p-6 border-t border-gray-100 flex justify-center bg-gray-50 flex-shrink-0">
                            <button
                                onClick={() => setIsRegistrantsModalOpen(false)}
                                className="px-8 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg shadow-slate-200 active:scale-95"
                            >
                                Close List
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Details Modal */}
            <ViewDetailsModal
                isOpen={isDetailsModalOpen}
                onClose={() => setIsDetailsModalOpen(false)}
                data={selectedTraining}
                type="training"
            />

            {/* Add Visitor Modal */}
            <Transition.Root show={isVisitorModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-[9999]" onClose={() => setIsVisitorModalOpen(false)}>
                    <Transition.Child as={Fragment}>
                        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                    </Transition.Child>
                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child as={Fragment}>
                                <Dialog.Panel className="w-full max-w-md bg-white rounded-[30px] shadow-2xl p-8">
                                    <div className="flex justify-between items-center mb-6">
                                        <Dialog.Title className="text-xl font-black text-slate-900 font-sans tracking-tight">Add Visitor for {selectedTraining?.training_title}</Dialog.Title>
                                        <button onClick={() => setIsVisitorModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-full text-slate-400">
                                            <XMarkIcon className="w-6 h-6" />
                                        </button>
                                    </div>
                                    <form onSubmit={handleAddVisitor} className="space-y-4">
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Visitor Name *</label>
                                            <input required type="text" value={visitorForm.name} onChange={e => setVisitorForm({ ...visitorForm, name: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Email *</label>
                                            <input required type="email" value={visitorForm.email} onChange={e => setVisitorForm({ ...visitorForm, email: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Contact Number *</label>
                                            <input required type="text" value={visitorForm.contact_number} onChange={e => setVisitorForm({ ...visitorForm, contact_number: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Company Name</label>
                                            <input type="text" value={visitorForm.company_name} onChange={e => setVisitorForm({ ...visitorForm, company_name: e.target.value })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Chapter *</label>
                                            <select
                                                required
                                                value={visitorForm.chapter}
                                                onChange={e => setVisitorForm({ ...visitorForm, chapter: e.target.value })}
                                                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary-500"
                                            >
                                                <option value="">Select Chapter</option>
                                                {chapters.map((ch) => (
                                                    <option key={ch.id} value={ch.name}>{ch.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* Payment Details */}
                                        {selectedTraining && Number(selectedTraining.training_charges || 0) > 0 && (
                                            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-3">
                                                <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider">Payment Options</h4>
                                                <div className="flex justify-between items-center text-sm font-semibold">
                                                    <span className="text-slate-500">Charges:</span>
                                                    <span className="text-slate-900 font-bold">₹{selectedTraining.training_charges}</span>
                                                </div>
                                                {selectedTraining.payment_link && (
                                                    <div className="text-xs font-semibold">
                                                        <span className="text-slate-500 block mb-1">Payment Link:</span>
                                                        <a 
                                                            href={selectedTraining.payment_link} 
                                                            target="_blank" 
                                                            rel="noreferrer" 
                                                            className="text-primary-600 hover:underline break-all inline-flex items-center gap-1"
                                                        >
                                                            <LinkIcon className="w-3.5 h-3.5" />
                                                            {selectedTraining.payment_link}
                                                        </a>
                                                    </div>
                                                )}
                                                <label className="flex items-center gap-3 cursor-pointer pt-2 border-t border-slate-200">
                                                    <input
                                                        type="checkbox"
                                                        checked={visitorForm.payment_confirmed}
                                                        onChange={(e) => setVisitorForm({ ...visitorForm, payment_confirmed: e.target.checked })}
                                                        className="w-4.5 h-4.5 rounded border-slate-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                                                    />
                                                    <span className="text-xs font-bold text-slate-700 select-none">Payment Done</span>
                                                </label>
                                            </div>
                                        )}
                                        <button
                                            type="submit"
                                            disabled={isVisitorSubmitting}
                                            className="w-full py-3 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-700 transition-all shadow-lg shadow-primary-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
                                        >
                                            {isVisitorSubmitting ? 'Adding Visitor...' : 'Add Visitor'}
                                        </button>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </div>
    );
};

export default Training;
